/**
 * @file src/ui/react/studio/LocalRailDialogs.tsx
 * @description The MISSING list — what the rail's "N missing" counter opens: every remembered file
 * the disk no longer has where it was, a Locate per row, and one "Search a folder" for all of them.
 * Not an error dialog: it never interrupts playback and can be left half-done.
 *
 * The one dialog this feature has. Importing raises none — whatever is dropped is analysed in the
 * background, and the rail's header says so in a line.
 *
 * Library components: `Dialog`, `Button`.
 */
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'plantasia.space-design/react';
import type { LocalRailProps } from './localRail';

type TFn = (key: string, opts?: Record<string, unknown>) => string;

const MUTED = 'var(--color-muted-foreground, rgba(255,255,255,0.55))';
const ROW_BORDER = '1px solid color-mix(in oklab, var(--color-foreground, #ffffff) 10%, transparent)';

export function LocalMissingDialog({ rail, t }: { rail: LocalRailProps; t: TFn }) {
  const count = rail.missingEntries.length;
  return (
    <Dialog open={rail.missingOpen} onOpenChange={(open) => rail.onSetMissingOpen(open)}>
      <DialogContent className="dark orb-studio__portal-surface" style={{ maxWidth: 520 }}>
        <DialogHeader>
          <DialogTitle>{t('studio.local.missingDialog.title', { count })}</DialogTitle>
          <DialogDescription>{t('studio.local.missingDialog.lead')}</DialogDescription>
        </DialogHeader>
        <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '50vh', overflowY: 'auto' }}>
          {rail.missingEntries.map((entry) => (
            <div
              key={entry.id}
              style={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: 12, padding: '9px 0', borderTop: ROW_BORDER }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.name}</div>
                {entry.folder ? (
                  <div style={{ fontSize: 11, color: MUTED, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.folder}</div>
                ) : null}
              </div>
              <Button variant="outline" size="sm" onClick={() => rail.onAction(entry.id, 'locate')}>
                {t('studio.local.missingDialog.locate')}
              </Button>
            </div>
          ))}
        </div>
        <DialogFooter style={{ alignItems: 'center', gap: 10 }}>
          <Button variant="outline" onClick={() => rail.onSearchFolder()} disabled={count === 0}>
            {t('studio.local.missingDialog.searchAll', { count })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
